/* eslint-disable max-len */
/* eslint-disable no-console */
/* eslint-disable jsx-a11y/anchor-is-valid */
/**
@format
@flow
*/

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Button } from '@ziynx/components/buttons';
import Modal from '@ziynx/components/modals';
import { useForm, Controller } from 'react-hook-form';
import Input, { InputAutocomplete } from '@ziynx/components/inputs';
import debounce from 'lodash.debounce';
import Dropdown from '@ziynx/components/selects';
import { Form, FormRow } from '@ziynx/components/forms';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { ContentContainer } from '@ziynx/components/layout';
import { useParams } from 'react-router-dom';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import { withAppManager, useNotification, LoadingSpinner } from '@ziynx/components/utilities';
import { Heading } from '@ziynx/components/typography';
import Link from '@ziynx/components/links';
import Colors from '@ziynx/theme/colors';
import ActionTypes from '@ziynx/action-types';
import { eventNames } from '@ziynx/analytics';
import type { Viewports } from '@ziynx/flow-types/globals';
import { TextEditor } from '@krispy-web/text-editor';
import * as KrispyTokens from '@krispy-tokens/blue';
import { EMPLOYMENT_TYPES } from '../../constants';

export interface Message {
  htmlString?: string;
  jsonState?: string;
}

type Props = $ReadOnly<{|
    /**
     * Viewport
     */
    viewport: Viewports,
    /**
     * Navigation stack
     */
    history: {
      push: (pathName: string, params?: { from: string }) => void,
      goBack: () => void,
      location: {
        state: {
          from: string,
        }
      },
    },

|}>;

const _modalWidth = (viewport) => {
  switch (viewport) {
  case 'mobile':
    return '333px';
  case 'mobileSmall':
    return '240px';
  default:
    return '488px';
  }
};

const modalButtonWidth = (viewport) => {
  switch (viewport) {
  case 'mobile':
    return '50%';
  case 'mobileSmall':
    return '100%';
  default:
    return '40%';
  }
};

const DiscardModal = styled(Modal)`
  padding: 24px;
  height: 180px;
  width: ${({ viewport }) => _modalWidth(viewport)};
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  height: 100%;
`;

const ModalButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  text-align: center;
  flex-direction: ${({ isMobileSmall }) => (isMobileSmall ? 'column' : 'row')};
`;

const ModalButton = styled(Button)`
  width: ${({ viewport }) => modalButtonWidth(viewport)};
  ${({ isMobileSmall, firstChild }) => !isMobileSmall && firstChild && 'margin-right: 16px;'}
  ${({ isMobileSmall, firstChild }) => isMobileSmall && firstChild && 'margin-bottom: 16px;'}
`;

const StyledHeading = styled(Heading)`
  padding-bottom: 16px;
`;

const PageWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: ${({ isMobileSmall }) => (isMobileSmall ? '40px 16px 60px' : '40px 40px 60px')};
`;

const PageContentWrapper = styled.div`
  width: 100%;
  max-width: 688px;
`;

const LinkWrapper = styled.div`
  margin-bottom: 24px;
`;

const CheckboxWrapper = styled.div`
  margin-left: 12px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: ${({ isMobileSmall }) => (isMobileSmall ? 'column' : 'row')};
  justify-content: space-between;
  align-items: center;
  margin-top: 40px;
`;

const StyledButton = styled(Button)`
  width: ${({ isMobileSmall }) => (isMobileSmall ? '100%' : '50%')};
  &:first-of-type {
    ${({ isMobileSmall }) => (isMobileSmall ? 'margin-bottom: 16px;' : 'margin-right: 16px;')}
  }
`;

const TextEditorWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  height: 100%;
  --text-editor-max-height-mobile: 180px;
  --text-editor-max-height-tablet-up: 262px;

  > div {
    width: 100%;
    border: 1px solid transparent;
    border-radius: 16px;
    min-height: 82px;
    min-width: 254px;
    a {
      color: ${KrispyTokens.KrispyColorElementsAccentOndark};
    }

    p {
      margin: 0;
    }
    i {
      font-style: italic;
    }
    
    :global .strikethrough {
      text-decoration: line-through;
    }
    
    ul {
      margin: 0;
      font-family: inherit;
      padding-inline-start: calc(${KrispyTokens.KrispySpacingElementsHdLarge}px + 8px);
    }
    
    li {
      font-family: inherit;
    }

    &:focus-visible, &:focus {
      outline: none;
      border: none;
    }
  }
`;

const JobPostScreen = (props: ManagedProps<Props>): React$Node => {
  const { viewport, history } = props;
  const { jobId } = useParams();
  const { notificationDispatch } = useNotification();
  const isMobileSmall = viewport === 'mobileSmall';
  const dispatch = useDispatch();
  const isEdit = !!jobId;
  const goBackTitle = history?.location?.state?.from || 'Jobs';
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  const [locationError, setLocationError] = useState(false);

  const {
    control, errors, clearErrors, reset, register, handleSubmit, formState: { dirtyFields, isDirty, isSubmitSuccessful },
  } = useForm();

  const jobItemService = useSelector(({ jobItem }) => {
    if (isEdit) return jobItem[jobId];
    return jobItem?.POST_JOB_API ?? {};
  }, shallowEqual);

  const [jobDescriptionString, setJobDescriptionString] = useState(jobItemService?.description || '');

  useEffect(() => {
    setJobDescriptionString(jobItemService?.description);
  }, [jobItemService?.description]);

  useEffect(() => {
    if (isEdit) {
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames.navigated_edit_job_post,
        properties: {
          referrer: history?.location?.state?.from,
          jobId,
        },
      });
    } else {
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames.navigated_create_job_post,
        properties: {
          referrer: history?.location?.state?.from,
        },
      });
    }
  }, [isEdit]);

  const isAutomated = jobItemService?.automated || false;

  // Needed to change checkbox control value. Don't use event.target .
  const [remoteLocation, setRemoteLocation] = useState(jobItemService?.remote || false);

  const searchService = useSelector(({ search }) => ({
    locations: search[ActionTypes.search.getJobLocation.GROUP] || {},
    interests: search[ActionTypes.search.getInterests.GROUP] || {},
    characterTraits: search[ActionTypes.search.getCharacterTraits.GROUP] || {},
  }), shallowEqual);

  const formatLocations = (locations) => (locations || []).map((item) => ({
    _id: item?.place_id,
    text: item?.name,
  }));

  const formatInterests = (interests) => (interests || []).map((item) => ({
    _id: item?._id,
    text: item?.name,
  }));

  const formatTraits = (traits) => (traits || []).map((item) => ({
    _id: item?._id ?? '',
    text: item?.value?.charAt(0)?.toUpperCase() + item?.value?.slice(1) ?? '',
  }));

  const [selectedLocations, setLocations] = useState(formatLocations(jobItemService?.locations || []));
  const [selectedInterests, setInterests] = useState(formatInterests(jobItemService?.career) || []);
  const [selectedTraits, setTraits] = useState(formatTraits(jobItemService?.characterTraits) || []);
  const [empType, setEmpType] = useState(jobItemService?.employmentType?._id || jobItemService?.employmentType || EMPLOYMENT_TYPES[0].value);

  const searchActionDispatch = (extraDispatch, field: string) => {
    let type: string = '';
    switch (field) {
    case 'locations':
      type = ActionTypes.search.getJobLocation.START;
      break;
    case 'interests':
      type = ActionTypes.search.getInterests.START;
      break;
    case 'characterTraits':
      type = ActionTypes.search.getCharacterTraits.START;
      break;
    default:
      break;
    }
    dispatch({
      type,
      ...extraDispatch,
    });
  };

  const onSearch = (field, value) => {
    const query = {
      name: value,
      page: 0,
    };

    searchActionDispatch({ query }, field);
  };

  const debouncedInterests = debounce((e) => onSearch('interests', e?.target?.value), 100);

  const debouncedTraits = debounce((e) => onSearch('characterTraits', e?.target?.value), 100);

  const onLocationSearch = (field, value) => {
    const query = {
      q: value,
    };

    searchActionDispatch({ query }, field);
  };

  /** USED FOR SELECTED AND OPTIONS ARRAY */
  const filterDuplicateLocations = (locations) => (
    (locations || []).filter((i) => !selectedLocations.some((j) => j._id === i._id))
  );

  const getArrayItems = (valueArray, selected = []) => (
    valueArray || []).filter((i) => !selected.some((j) => j._id === i._id)).map((item) => (
    {
      ...!!item?._id && { _id: item?._id },
      text: item?.name ? item?.name : item, // Use text because data structure of buttonPillGroup
    }));

  const filterDuplicateTraits = (traits) => (
    (traits || []).filter((i) => !selectedTraits.some((j) => j._id === i._id))
  );

  useEffect(() => {
    if (!jobItemService?.fetching && jobItemService?.locations) {
      setLocations(formatLocations(jobItemService?.locations));
    }
  }, [jobItemService?.locations]);

  useEffect(() => {
    if (!jobItemService?.fetching && jobItemService?.career) {
      setInterests(formatInterests(jobItemService?.career));
    }
  }, [jobItemService?.career]);

  useEffect(() => {
    if (!jobItemService?.fetching && jobItemService?.success) {
      setTraits(formatTraits(jobItemService?.characterTraits));
    }
  }, [jobItemService?.characterTraits]);

  useEffect(() => {
    if (isEdit) {
      dispatch({
        type: ActionTypes.jobItem.getJob.START,
        id: jobId,
      });
    }
  }, [isEdit]);

  useEffect(() => {
    if (isEdit) {
      reset({
        title: jobItemService?.title,
        locations: selectedLocations,
        description: jobItemService?.description,
        // Fallback if employment type isn't populated
        employmentType: jobItemService?.employmentType?._id || jobItemService?.employmentType,
        link: jobItemService?.applyLink,
        interests: selectedInterests,
        characterTraits: selectedTraits,
        remote: jobItemService?.remote,
      }, {
        dirtyFields: false,
      });
    } else return undefined;
  }, [jobItemService?.success, jobItemService?.error, isEdit]);

  useEffect(() => {
    if (!jobItemService?.fetching && jobItemService?.success && !isEdit) {
      dispatch({ type: ActionTypes.jobItem.clear });
      history.push('/jobs', { from: '/jobs/create-post' });
    }
  }, [
    jobItemService?.fetching,
    jobItemService?.success,
    isEdit,
  ]);

  useEffect(() => {
    if (!jobItemService?.patching && jobItemService?.patchSuccess && isEdit) {
      dispatch({
        type: ActionTypes.jobItem.clear,
        id: jobId,
      });
      dispatch({ type: ActionTypes.job.patchedJob });
      history.push('/jobs', { from: '/jobs/create-post' });
    }
  }, [
    jobItemService?.patching,
    jobItemService?.patchSuccess,
    isEdit,
  ]);

  useEffect(() => {
    if (Object.keys(errors).length > 0 || locationError) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'error',
          content: 'Please fill out all required fields',
        },
      });
    }
  }, [errors]);

  const INPUT_FIELDS = [
    'title',
    'locations',
    'description',
    'employmentType',
    'applyLink',
    'interests',
    'remote',
    'referenceId',
    'characterTraits',
  ];

  const _getPatchOps = ({ data }) => {
    const ops = [];

    INPUT_FIELDS.forEach((field) => {
      if (!dirtyFields[field]) return;
      if (field === 'title' && data?.title) {
        ops.push(
          {
            op: 'replace',
            path: 'title',
            value: data?.title.trim(),
          },
        );
      } else if (field === 'locations' && data?.locations) {
        ops.push(
          {
            op: 'replace',
            path: 'locations',
            value: data?.locations.map((item) => ({
              place_id: item?._id,
              name: item?.text,
            })),
          },
        );
      } else if (jobDescriptionString?.length > 0 && jobItemService?.description !== jobDescriptionString) {
        ops.push(
          {
            op: 'replace',
            path: 'description',
            value: jobDescriptionString || '',
          },
        );
      } else if (field === 'employmentType' && data?.employmentType) {
        ops.push(
          {
            op: 'replace',
            path: 'employmentType',
            value: data?.employmentType,
          },
        );
      } else if (field === 'applyLink' && data?.applyLink) {
        ops.push(
          {
            op: 'replace',
            path: 'applyLink',
            value: data?.applyLink?.trim(),
          },
        );
      } else if (field === 'referenceId') {
        ops.push(
          {
            op: 'replace',
            path: 'referenceId',
            value: data?.referenceId,
          },
        );
      } else if (field === 'interests' && data?.interests) {
        ops.push(
          {
            op: 'replace',
            path: 'career',
            value: data?.interests,
          },
        );
      } else if (field === 'remote') {
        ops.push(
          {
            op: 'replace',
            path: 'remote',
            value: data?.remote,
          },
        );
      } else if (field === 'characterTraits' && data?.characterTraits) {
        ops.push(
          {
            op: 'replace',
            path: 'characterTraits',
            value: data?.characterTraits,
          },
        );
      }
    });
    return ops;
  };

  const onSubmit = async (data) => {
    if ((selectedLocations.length > 0) || remoteLocation) {
      setLocationError(false);
      const dataObj = {
        ...data,
        applyLink: data?.applyLink?.trim(),
      };
      const patchOps = isEdit ? _getPatchOps({ data }) : null;
      const {
        title, employmentType, description, applyLink, referenceId, interests, remote, characterTraits, locations,
      } = dataObj;

      if (isEdit) {
        dispatch({
          type: ActionTypes.analytics.logEvent.START,
          name: eventNames.edit_job_post,
        });
      } else {
        dispatch({
          type: ActionTypes.analytics.logEvent.START,
          name: eventNames.created_job_post,
        });
      }
      if (isEdit) {
        dispatch({
          type: ActionTypes.jobItem.patchJob.START,
          body: patchOps,
          id: jobId,
        });
      } else if (!remoteLocation) {
        dispatch({
          type: ActionTypes.jobItem.postJob.START,
          body: {
            title,
            locations: locations.map((item) => ({
              place_id: item?._id,
              name: item?.text,
            })),
            description,
            employmentType,
            applyLink,
            referenceId,
            career: interests,
            characterTraits,
            remote,
          },
        });
      } else if (selectedLocations.length > 0 && remoteLocation) {
        dispatch({
          type: ActionTypes.jobItem.postJob.START,
          body: {
            title,
            locations: locations.map((item) => ({
              place_id: item?._id,
              name: item?.text,
            })),
            description,
            employmentType,
            applyLink,
            referenceId,
            career: interests,
            characterTraits,
            remote,
          },
        });
      } else {
        dispatch({
          type: ActionTypes.jobItem.postJob.START,
          body: {
            title,
            description,
            employmentType,
            applyLink,
            referenceId,
            career: interests,
            characterTraits,
            remote,
          },
        });
      }

      if (jobItemService?.error) {
        notificationDispatch({
          type: ActionTypes.notifications.add.START,
          payload: {
            variant: 'error',
            content: 'Something went wrong. Please try again.',
          },
        });
      }
    } else {
      setLocationError(true);
    }
  };

  const capFirstTraitLetter = (trait) => trait[0].toUpperCase() + trait.slice(1);

  const traitsScroll = (e, { hasReachedEnd }) => {
    if (searchService?.characterTraits?.isLastPage) return;
    if (hasReachedEnd) {
      dispatch({
        type: ActionTypes.search.getCharacterTraits.START,
        query: { page: searchService?.characterTraits?.page + 1 },
      });
    }
  };

  const interestsScroll = (e, { hasReachedEnd }) => {
    if (searchService?.interests?.isLastPage) return;
    if (hasReachedEnd) {
      dispatch({
        type: ActionTypes.search.getInterests.START,
        query: { page: searchService?.interests?.page + 1 },
      });
    }
  };

  const handleTraitsFocus = () => {
    onSearch('characterTraits', '');
  };

  const handleInterestsFocus = () => {
    onSearch('interests', '');
  };

  const _onChangeJobDescription = (message: Message) => {
    if (message?.htmlString) setJobDescriptionString(message?.htmlString);
  };

  return (
    <>
      <Helmet>
        <title>Customize job post | ZIYNX</title>
      </Helmet>
      <ContentContainer isVertical>
        <PageWrapper isMobileSmall={isMobileSmall}>
          <PageContentWrapper>
            <LinkWrapper>
              <Link
                variant="caret"
                onClick={(isDirty && !isSubmitSuccessful) ? () => setDiscardModalOpen(true) : () => {
                  history.goBack();
                }}
                color={Colors.GrayDark}
              >
                Back to
                {' '}
                {goBackTitle}
              </Link>
            </LinkWrapper>
            <Form
              formID="customize-job-post"
              title="Customize job post"
              width="100%"
            >
              <FormRow paddingBottom="32px">
                <Controller
                  control={control}
                  render={({
                    onFocus, onChange, onBlur, value,
                  }) => {
                    const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
                      (event?.target: HTMLInputElement);
                      onChange(event);
                    };

                    const onInputFocus = (): any => {
                      if (errors?.title) clearErrors('title');
                      return onFocus;
                    };
                    return (
                      <Input
                        variant="text"
                        autoComplete="off"
                        error={!!errors?.title}
                        disabled={jobItemService?.fetching}
                        errorLabel={errors?.title?.message}
                        inputName="title"
                        charLimit={!isAutomated ? 65 : undefined}
                        maxLength={!isAutomated ? 65 : undefined}
                        initialCount={jobItemService?.title?.length || 0}
                        label="Job title"
                        required
                        onBlur={onBlur}
                        onChange={onInputChange}
                        onFocus={onInputFocus}
                        value={value}
                      />
                    );
                  }}
                  name="title"
                  defaultValue={isEdit ? jobItemService?.title || ' ' : ''}
                  rules={{
                    required: 'Job title required.',
                  }}
                />
              </FormRow>
              <FormRow paddingBottom="16px" height="100%">
                <InputAutocomplete
                  inputName="locations"
                  required
                  label="Location"
                  width="auto"
                  disabled={jobItemService?.fetching}
                  error={locationError}
                  errorLabel="Location is required"
                  helperText="Search for cities in US"
                  // showOptionsOnFocus
                  // {...!(searchService?.locations?.results ?? []).length && {
                  //   onFocus: () => onLocationSearch('location', 'o'),
                  // }}
                  isLoading={searchService?.locations?.fetching}
                  onChange={(e) => onLocationSearch('locations', e?.target?.value)}
                  options={filterDuplicateLocations(formatLocations(searchService?.locations?.results))}
                  onClick={(e, option) => {
                    const newLocations = [...selectedLocations, option];
                    setLocations(newLocations);
                    control?.setValue('locations', newLocations, { shouldDirty: true });
                    setLocationError(false);
                  }}
                  selected={selectedLocations}
                  onSelectedClick={(e, option) => {
                    const newLocations = selectedLocations.filter((item) => item?._id !== option?._id);
                    setLocations(newLocations);
                    control?.setValue('locations', newLocations || [], { shouldDirty: true });
                    setLocationError(!newLocations.length && !remoteLocation);
                  }}
                  {...register({ name: 'locations', type: 'custom' })}
                />
              </FormRow>
              <FormRow height="100%" paddingBottom="32px">
                <CheckboxWrapper>
                  <Input
                    variant="checkbox"
                    squircle
                    error={!!errors?.remote}
                    disabled={jobItemService?.fetching}
                    errorLabel={errors?.remote?.message}
                    inputName="remote"
                    label="Remote location"
                    labelColor={Colors.GrayVeryDark}
                    previouslyChecked={remoteLocation}
                    onChange={() => {
                      const flipped = !remoteLocation;
                      control?.setValue('remote', flipped, { shouldDirty: jobItemService?.remote !== flipped });
                      setRemoteLocation(flipped);
                      setLocationError(!selectedLocations.length && !flipped);
                    }}
                    {...register({ name: 'remote', type: 'custom' })}
                  />
                </CheckboxWrapper>
              </FormRow>
              <FormRow paddingBottom="32px" height="100%">
                <InputAutocomplete
                  inputName="interests"
                  label="Job function"
                  width="auto"
                  disabled={jobItemService?.fetching}
                  onScroll={interestsScroll}
                  error={!!errors?.interests}
                  errorLabel={errors?.interests?.message}
                  helperText="Adding job function will help talent discover roles that match their career interests."
                  showOptionsOnFocus
                  {...!(searchService?.interests?.results ?? []).length && {
                    onFocus: () => onSearch('interests', 'e'),
                  }}
                  onFocus={handleInterestsFocus}
                  isLoading={searchService?.interests?.fetching}
                  isPaginating={searchService?.interests?.paginating}
                  onChange={debouncedInterests}
                  options={
                    getArrayItems(searchService?.interests?.results, selectedInterests)
                  }
                  onClick={(e, option) => {
                    const newInterests = [...selectedInterests, option];
                    setInterests(newInterests);
                    control?.setValue('interests', newInterests, { shouldDirty: true });
                  }}
                  selected={selectedInterests}
                  onSelectedClick={(e, option) => {
                    const newInterests = selectedInterests.filter((item) => item?._id !== option?._id);
                    setInterests(newInterests);
                    control?.setValue('interests', newInterests || [], { shouldDirty: true });
                  }}
                  {...register({ name: 'interests', type: 'custom' })}
                />
              </FormRow>
              <FormRow paddingBottom="32px" height="100%">
                <InputAutocomplete
                  inputName="characterTraits"
                  label="Character traits"
                  width="auto"
                  disabled={jobItemService?.fetching}
                  onScroll={traitsScroll}
                  isPaginating={searchService?.characterTraits?.paginating}
                  error={!!errors?.characterTraits}
                  errorLabel={errors?.characterTraits?.message}
                  helperText="Adding traits that you value in candidates for this job will help match talent to roles."
                  showOptionsOnFocus
                  {...!(searchService?.characterTraits?.results ?? []).length && {
                    onFocus: () => onSearch('characterTraits', ''),
                  }}
                  onFocus={handleTraitsFocus}
                  isLoading={searchService?.characterTraits?.fetching}
                  onChange={debouncedTraits}
                  options={
                    filterDuplicateTraits(formatTraits(searchService?.characterTraits?.results))
                  }
                  onClick={(e, option) => {
                    const capLetter = capFirstTraitLetter(option?.text);
                    const capOption = {
                      ...option,
                      text: capLetter,
                    };
                    const newTraits = [...selectedTraits, capOption];
                    setTraits(newTraits);
                    control?.setValue('characterTraits', newTraits, { shouldDirty: true });
                  }}
                  selected={selectedTraits}
                  onSelectedClick={(e, option) => {
                    const newTraits = selectedTraits.filter((item) => item?._id !== option?._id);
                    setTraits(newTraits);
                    control?.setValue('characterTraits', newTraits || [], { shouldDirty: true });
                  }}
                  {...register({ name: 'characterTraits', type: 'custom' })}
                />
              </FormRow>
              <FormRow paddingBottom="32px" height="100%">
                {control?.setValue('employmentType', empType, { shouldDirty: false })}
                <Dropdown
                  activeLabel="Employment-type"
                  label="Employment-type"
                  inputName="employmentType"
                  error={!!errors?.employmentType}
                  errorLabel={errors?.employmentType?.message}
                  disabled={jobItemService?.fetching}
                  required
                  defaultValue={empType}
                  onChange={(event: SyntheticInputEvent<HTMLInputElement>): void => {
                    (event?.target: HTMLInputElement);
                    setEmpType(event);
                    control?.setValue('employmentType', event, { shouldDirty: true });
                  }}
                  options={EMPLOYMENT_TYPES}
                  {...register({ name: 'employmentType', type: 'custom' })}
                />
              </FormRow>
              <FormRow height="100%">
                <TextEditorWrapper>
                  <TextEditor
                    onChange={_onChangeJobDescription}
                    editable={jobItemService?.fetching || jobItemService?.isPatching}
                    initialValue={jobItemService?.description}
                    placeholderText="Job Description"
                  />
                </TextEditorWrapper>
              </FormRow>
              <FormRow paddingBottom="32px">
                {isEdit && !jobItemService?.applyLink ? <LoadingSpinner /> : (
                  <Controller
                    control={control}
                    render={({
                      onFocus, onChange, onBlur, value,
                    }) => {
                      const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
                        (event?.target: HTMLInputElement);
                        onChange(event);
                      };

                      const onInputFocus = (): any => {
                        if (errors?.applyLink) clearErrors('applyLink');
                        return onFocus;
                      };
                      return (
                        <Input
                          variant="text"
                          autoComplete="off"
                          error={!!errors?.applyLink}
                          disabled={jobItemService?.fetching}
                          errorLabel={errors?.applyLink?.message}
                          inputName="applyLink"
                          initialCount={jobItemService?.applyLink?.length || 0}
                          required
                          label="Link to apply"
                          onBlur={onBlur}
                          onChange={onInputChange}
                          onFocus={onInputFocus}
                          value={value}
                        />
                      );
                    }}
                    name="applyLink"
                    defaultValue={isEdit ? jobItemService?.applyLink : ''}
                    rules={{
                      required: 'Link required.',
                      pattern: {
                        value: /[-a-zA-Z0-9@:%._\\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)?/,
                        message: 'Must be a valid url.',
                      },
                    }}
                  />
                )}
              </FormRow>
              <FormRow paddingBottom="32px">
                <Controller
                  control={control}
                  render={({
                    onFocus, onChange, onBlur, value,
                  }) => {
                    const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
                      (event?.target: HTMLInputElement);
                      onChange(event);
                    };

                    const onInputFocus = (): any => {
                      if (errors?.referenceId) clearErrors('referenceId');
                      return onFocus;
                    };
                    return (
                      <Input
                        variant="text"
                        autoComplete="off"
                        error={!!errors?.referenceId}
                        disabled={jobItemService?.fetching}
                        errorLabel={errors?.referenceId?.message}
                        inputName="referenceId"
                        initialCount={jobItemService?.referenceId?.length || 0}
                        label="Reference no./ID"
                        onBlur={onBlur}
                        onChange={onInputChange}
                        onFocus={onInputFocus}
                        value={value}
                      />
                    );
                  }}
                  name="referenceId"
                  defaultValue={isEdit ? jobItemService?.referenceId || '' : ''}
                />
              </FormRow>
            </Form>
            {!isEdit && (
              <ButtonWrapper isMobileSmall={isMobileSmall}>
                <StyledButton
                  isMobileSmall={isMobileSmall}
                  onClick={handleSubmit(onSubmit)}
                  disabled={
                    Object.keys(dirtyFields).length === 0
                    || Object.keys(errors).length > 0
                    || jobItemService?.fetching
                    || (empType === '')
                  }
                  isLoading={jobItemService?.fetching}
                  width="50%"
                >
                  Create post
                </StyledButton>
                <StyledButton
                  isMobileSmall={isMobileSmall}
                  variant="primary"
                  width="50%"
                  inverted
                  disabled={jobItemService?.fetching}
                  onClick={(isDirty && !isSubmitSuccessful)
                    ? () => setDiscardModalOpen(true)
                    : () => {
                      dispatch({ type: ActionTypes.jobItem.clear });
                      history.goBack();
                    }}
                >
                  Cancel
                </StyledButton>
              </ButtonWrapper>
            )}
            {isEdit && (
              <ButtonWrapper isMobileSmall={isMobileSmall}>
                {!isAutomated ? (
                  <StyledButton
                    isMobileSmall={isMobileSmall}
                    disabled={
                      Object.keys(dirtyFields).length === 0
                    || Object.keys(errors).length > 0
                    || jobItemService?.fetching
                    || (empType === '')
                    }
                    onClick={handleSubmit(onSubmit)}
                    isLoading={jobItemService?.fetching}
                    width="50%"
                  >
                    Save
                  </StyledButton>
                ) : (
                  <StyledButton
                    isMobileSmall={isMobileSmall}
                    disabled={
                      Object.keys(errors).length > 0
                    || jobItemService?.fetching
                    }
                    onClick={handleSubmit(onSubmit)}
                    isLoading={jobItemService?.fetching}
                    width="50%"
                  >
                    Save
                  </StyledButton>
                )}
                <StyledButton
                  isMobileSmall={isMobileSmall}
                  variant="primary"
                  width="50%"
                  disabled={jobItemService?.fetching}
                  inverted
                  onClick={(isDirty && !isSubmitSuccessful)
                    ? () => setDiscardModalOpen(true)
                    : () => {
                      dispatch({ type: ActionTypes.jobItem.clear });
                      history.goBack();
                    }}
                >
                  Cancel
                </StyledButton>
              </ButtonWrapper>
            )}
          </PageContentWrapper>
        </PageWrapper>
        <DiscardModal open={discardModalOpen} viewport={viewport}>
          <ModalContent>
            <StyledHeading small>Are you sure you want to discard your changes?</StyledHeading>
            <ModalButtonWrapper isMobileSmall={isMobileSmall}>
              <ModalButton
                width="100%"
                viewport={viewport}
                isMobileSmall={isMobileSmall}
                firstChild
                onClick={() => {
                  history.goBack();
                }}
              >
                Back to
                {' '}
                {goBackTitle}
              </ModalButton>
              <ModalButton
                viewport={viewport}
                inverted
                width="100%"
                onClick={() => setDiscardModalOpen(false)}
                isMobileSmall={isMobileSmall}
              >
                Cancel
              </ModalButton>
            </ModalButtonWrapper>
          </ModalContent>
        </DiscardModal>
      </ContentContainer>
    </>
  );
};

export default (withAppManager(JobPostScreen): React$ComponentType<ManagedProps<Props>>);
